import { InsightsRepository as IInsightsRepository } from '@domain/insights/IInsightsRepository';
import { InsightsDataSource } from '../datasources/InsightsDataSource';
import { Rank } from '@domain/insights/models/Rank';
import { WeeklySearchVolume as WeeklySearchVolume } from '@domain/insights/models/SearchTrend';
import { Search } from '@domain/insights/models/Search';
import { DailyProductRank } from '@domain/insights/models/DailyProductRank';
import { Resource, PaginatedResource, PaginationRequest } from '@domain/base/Resource';
import { InsightsMapper } from '../mappers/InsightsMapper';

export class InsightsRepository implements IInsightsRepository {
  constructor(private dataSource: InsightsDataSource) { }

  async getRanksByProductId(productId: string): Promise<Resource<Rank[]>> {
    try {
      const data = await this.dataSource.getRanksByProductId(productId);
      return {
        status: 'success',
        data: data
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch ranks'
        }
      };
    }
  }

  async getRanksBySearchTerm(searchTerm: string): Promise<Resource<Rank[]>> {
    try {
      const data = await this.dataSource.getRanksBySearchTerm(searchTerm);
      return {
        status: 'success',
        data: data.map(InsightsMapper.rankToModel)
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch ranks'
        }
      };
    }
  }


  async updateRankSettings(
    productId: string,
    searchTerm: string,
    useForForecasting?: boolean,
    excludeFromForecasting?: boolean
  ): Promise<Resource<Rank>> {
    try {
      const data = await this.dataSource.updateRankSettings(
        productId,
        searchTerm,
        useForForecasting,
        excludeFromForecasting
      );
      return {
        status: 'success',
        data: data
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to update rank settings'
        }
      };
    }
  }

  async getSearchVolume(searchTerm: string, countryCode: string): Promise<Resource<WeeklySearchVolume[]>> {
    try {
      const data = await this.dataSource.getSearchVolume(searchTerm, countryCode);
      return {
        status: 'success',
        data: data
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch search trends'
        }
      };
    }
  }

  async getSearchTerms(
    connectorTypes?: string[],
    searchTerm?: string,
    pagination?: PaginationRequest
  ): Promise<PaginatedResource<Search[]>> {
    try {
      const paginatedData = await this.dataSource.getSearchTerms(connectorTypes, searchTerm, pagination);
      return {
        status: 'success',
        data: paginatedData.data,
        page: paginatedData.page,
        limit: paginatedData.limit,
        total: paginatedData.total,
        totalPages: paginatedData.totalPages,
        hasNext: paginatedData.hasNext,
        hasPrevious: paginatedData.hasPrevious
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch search terms'
        }
      };
    }
  }

  async getSearchTerm(searchTerm: string): Promise<Resource<Search>> {
    try {
      const data = await this.dataSource.getSearchTerm(searchTerm);
      return {
        status: 'success',
        data: data
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch search term'
        }
      };
    }
  }

  async getRankById(rankId: string): Promise<Resource<Rank>> {
    try {
      const data = await this.dataSource.getRankById(rankId);
      return {
        status: 'success',
        data: data
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch daily product ranks'
        }
      };
    }
  }
} 